import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { REASON_LABEL } from "@/components/chat/answer-rating";
import { HBars, Sparkline, StatusChip, TrendChart, asPct } from "@/components/evaluations/charts";
import { KpiRow, PeriodSelect, dayLabel, filterSelect } from "@/components/evaluations/parts";
import { ScoringSettings } from "@/components/evaluations/scoring-settings";
import { PageHeader } from "@/components/layout-bits";
import { Link } from "@/components/ui/link";
import { Card, Input, Skeleton } from "@/components/ui/primitives";
import { EmptyState, ErrorState } from "@/components/ui/states";
import { getEvaluationSummary } from "@/lib/api/chat";
import { useMe } from "@/lib/query/hooks";
import type { AgentScore } from "@/types/api";

const SERIES = { name: "Approval", color: "var(--series-1)" };

type Sort = "attention" | "rated" | "name";

/**
 * Evaluations (AVI-171 follow-up): how agents' answers are rated by evaluators
 * — across all agents first, then a card per agent that opens
 * that agent's own page.
 */
export default function EvaluationsPage() {
  const [days, setDays] = React.useState(30);
  const [search, setSearch] = React.useState("");
  const [sort, setSort] = React.useState<Sort>("attention");
  const { data: me } = useMe();

  const q = useQuery({
    queryKey: ["my", "evaluations", days],
    queryFn: () => getEvaluationSummary(days),
  });

  const agents = React.useMemo(() => {
    const list = (q.data?.agents ?? []).filter((a) =>
      a.name.toLowerCase().includes(search.trim().toLowerCase()),
    );
    if (sort === "rated") return [...list].sort((a, b) => b.up + b.down - (a.up + a.down));
    if (sort === "name") return [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list; // the API already puts agents needing attention first
  }, [q.data, search, sort]);

  return (
    <div>
      <PageHeader
        title="Evaluations"
        description="How agents' answers are rated by evaluators in the chat app."
        actions={
          <div className="flex flex-wrap gap-2">
            <PeriodSelect value={days} onChange={setDays} />
          </div>
        }
      />

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.isLoading || !q.data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <div className="grid gap-6">
          <KpiRow
            kpis={q.data.kpis}
            days={days}
            third={{
              label: "Agents needing attention",
              value: q.data.agents.filter((a) => a.status === "attention").length,
              sub: `of ${q.data.agents.filter((a) => a.status !== "no_data").length} rated agents`,
            }}
          />

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            <Card className="grid gap-2 p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold">Approval rate, all agents</h3>
                <span className="text-xs text-muted-foreground">
                  % of ratings that are 👍, per day
                </span>
              </div>
              <TrendChart
                labels={q.data.trend.map((p) => dayLabel(p.date))}
                series={[{ ...SERIES, data: q.data.trend.map((p) => p.approval) }]}
              />
            </Card>
            <Card className="grid content-start gap-3 p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-semibold">Why answers were rated down</h3>
                <span className="text-xs text-muted-foreground">last {days} days</span>
              </div>
              {q.data.kpis.down ? (
                <HBars
                  rows={q.data.reasons.map((r) => ({
                    label: REASON_LABEL[r.category],
                    value: r.count,
                    hint: `${REASON_LABEL[r.category]}: ${r.count} thumbs-downs`,
                  }))}
                />
              ) : (
                <p className="text-sm text-muted-foreground">No thumbs-downs in this period.</p>
              )}
            </Card>
          </div>

          <div className="grid gap-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold">Agents</h2>
              <div className="flex flex-wrap gap-2">
                <Input
                  aria-label="Search agents"
                  placeholder="Search agents"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 w-48"
                />
                <select
                  aria-label="Sort agents"
                  className={filterSelect}
                  value={sort}
                  onChange={(e) => setSort(e.target.value as Sort)}
                >
                  <option value="attention">Needs attention first</option>
                  <option value="rated">Most rated</option>
                  <option value="name">Name</option>
                </select>
              </div>
            </div>
            {agents.length === 0 ? (
              <EmptyState
                title={search ? "No agents match" : "No agents yet"}
                hint="Agents appear here once they exist; their numbers once their answers are rated."
              />
            ) : (
              <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(240px,1fr))]">
                {agents.map((a) => (
                  <AgentCard key={a.slug} agent={a} />
                ))}
              </div>
            )}
          </div>

          {me?.role === "tenant_admin" ? <ScoringSettings /> : null}
        </div>
      )}
    </div>
  );
}

function AgentCard({ agent: a }: { agent: AgentScore }) {
  return (
    <Link
      href={`/evaluations/${a.slug}`}
      className="grid gap-2 rounded-lg border bg-card p-4 transition-colors hover:border-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-semibold">{a.name}</p>
          <p className="font-mono text-[11px] text-muted-foreground">
            {a.live_version ? `live v${a.live_version}` : "no live version"}
          </p>
        </div>
        <StatusChip status={a.status} />
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold tabular-nums">{asPct(a.approval_rate)}</span>
        <span className="text-xs text-muted-foreground">approval</span>
      </div>
      <Sparkline data={a.trend} color="var(--series-1)" />
      {a.scored ? (
        <span className="text-xs text-muted-foreground">
          Accuracy <span className="font-medium text-foreground">{asPct(a.accuracy_rate)}</span>{" "}
          High · {a.scored} scored
        </span>
      ) : null}
      <div className="flex flex-wrap justify-between gap-2 text-xs text-muted-foreground">
        <span className="tabular-nums">
          👍 {a.up} · 👎 {a.down}
        </span>
        <span>
          {a.top_reason ? `Top issue: ${REASON_LABEL[a.top_reason]}` : "No issues reported"}
        </span>
      </div>
      <span className="text-xs text-primary">Open →</span>
    </Link>
  );
}
